import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { ProductOrder } from '../../interfaces/Product/GetProductsRequest';
import { sharedColors } from '../../utils/Style';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type SortProductsHeaderProps = {
  loading: boolean;
  totalCount: number;
  order: ProductOrder;
  onChangeOrder: (newOrder: ProductOrder) => void;
  filterCount?: number;
  onFilter?: () => void;
};

export const SortProductsHeader = ({
  loading,
  totalCount,
  order,
  onChangeOrder,
  filterCount,
  onFilter,
}: SortProductsHeaderProps) => {
  const t = useLocalizedText();

  const isMobile = useIsMobile();

  const textStyle = {
    fontSize: '14px',
    fontWeight: 400,
    color: sharedColors.gray6,
  };

  const productOrderNameMap: Record<ProductOrder, string> = {
    orderByQuantityDesc: t('product.sort_header.options.default'),
    orderByPriceAsc: t('product.sort_header.options.price_asc'),
    orderByPriceDesc: t('product.sort_header.options.price_desc'),
    orderByNameAsc: t('product.sort_header.options.name_asc'),
    orderByNameDesc: t('product.sort_header.options.name_desc'),
  };

  const showFilterButton = isMobile && !!onFilter;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        flexWrap: 'wrap',
        backgroundColor: sharedColors.gray2,
        border: '1px solid #e1e1e1',
        p: 2,
        boxSizing: 'border-box',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Typography sx={textStyle}>
        {loading
          ? t('product.sort_header.loading')
          : t('product.sort_header.displaying', { count: totalCount })}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          flexGrow: 1,
          justifyContent: 'end',
        }}
      >
        {!showFilterButton && (
          <Typography sx={textStyle}>
            {t('product.sort_header.sort_products')}
          </Typography>
        )}

        <Select
          value={order}
          size='small'
          onChange={(e) => onChangeOrder(e.target.value as ProductOrder)}
          fullWidth={isMobile}
          sx={{
            fontSize: '14px',
            minWidth: 200,
          }}
        >
          {Object.entries(productOrderNameMap).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </Select>

        {showFilterButton && (
          <Button
            variant='outlined'
            color='inherit'
            onClick={() => onFilter?.()}
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              width: 130,
              color: sharedColors.gray7,
              border: '1px solid #d1d1d1',
            }}
          >
            {t('product.sort_header.filter', {
              count: filterCount ? `(${filterCount})` : '',
            })}
          </Button>
        )}
      </Box>
    </Box>
  );
};
